/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { orderBy } from 'lodash';
import { Drawer, Box, Divider, Tooltip, IconButton, Typography, Badge, ListItemButton, ListItemText } from '@mui/material';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Iconify } from 'src/components';
import Scrollbar from 'src/components/Scrollbar';

function ColorSwatches({ options = [], value = [], onChange }) {
  const isSelected = (c) => value.includes(c);
  const toggle = (c) => {
    const next = isSelected(c) ? value.filter((v) => v !== c) : [...value, c];
    onChange(next);
  };
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {options.map((c) => (
        <Box
          key={c}
          onClick={() => toggle(c)}
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            cursor: 'pointer',
            border: (theme) => `solid 2px ${isSelected(c) ? c : theme.palette.divider}`,
            bgcolor: c,
          }}
        />
      ))}
    </Box>
  );
}

export default function CalendarFilters({
  open,
  onClose,
  events = [],
  colors = [],
  onChangeColors,
  startDate,
  endDate,
  onChangeStartDate,
  onChangeEndDate,
  canReset,
  onReset,
  dateError,
  onClickEvent,
}) {
  const ordered = useMemo(() => orderBy(events, ['end'], ['desc']), [events]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      BackdropProps={{ invisible: true }}
      PaperProps={{ sx: { width: 320 } }}
    >
      <Box sx={{ py: 2, pr: 1, pl: 2.5, display: 'flex', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Filters
        </Typography>
        <Tooltip title="Reset">
          <IconButton onClick={onReset}>
            <Badge color="error" variant="dot" invisible={!canReset}>
              <Iconify icon="solar:restart-bold" />
            </Badge>
          </IconButton>
        </Tooltip>
        <IconButton onClick={onClose}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Box>
      <Divider sx={{ borderStyle: 'dashed' }} />

      <Scrollbar>
        <Box sx={{ my: 3, px: 2.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Colors
          </Typography>
          <ColorSwatches options={colors?.options || []} value={colors?.value || []} onChange={onChangeColors} />
        </Box>

        <Box sx={{ mb: 3, px: 2.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Range
          </Typography>
          <DatePicker label="Start date" value={startDate} onChange={onChangeStartDate} sx={{ mb: 2.5 }} />
          <DatePicker
            label="End date"
            value={endDate}
            onChange={onChangeEndDate}
            slotProps={{ textField: { error: dateError, helperText: dateError ? 'End date must be later than start date' : null } }}
          />
        </Box>

        <Box sx={{ px: 2.5, mb: 1 }}>
          <Typography variant="subtitle2">Events ({ordered.length})</Typography>
        </Box>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {ordered.map((event) => {
            const color = event.color || event.textColor || '#919EAB';
            const primary = event.allDay
              ? format(new Date(event.start), 'd MMM yyyy')
              : `${format(new Date(event.start), 'd MMM yyyy p')} - ${format(new Date(event.end || event.start), 'd MMM yyyy p')}`;
            return (
              <Box component="li" key={event.id}>
                <ListItemButton
                  onClick={() => onClickEvent?.(String(event.id))}
                  sx={(theme) => ({
                    alignItems: 'flex-start',
                    py: 1.5,
                    position: 'relative',
                    borderBottom: `dashed 1px ${theme.palette.divider}`,
                    '&:hover': { bgcolor: theme.palette.action.hover },
                  })}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      left: 0,
                      width: 0,
                      height: 0,
                      borderRight: '10px solid transparent',
                      borderTop: `10px solid ${color}`,
                    }}
                  />
                  <ListItemText
                    primary={primary}
                    secondary={event.title}
                    primaryTypographyProps={{ sx: { typography: 'caption', color: 'text.disabled' } }}
                    secondaryTypographyProps={{ sx: { mt: 0.5, color: 'text.primary', typography: 'subtitle2' } }}
                  />
                </ListItemButton>
              </Box>
            );
          })}
        </Box>
      </Scrollbar>
    </Drawer>
  );
}

CalendarFilters.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  events: PropTypes.array,
  colors: PropTypes.shape({ options: PropTypes.array, value: PropTypes.array }),
  onChangeColors: PropTypes.func,
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  onChangeStartDate: PropTypes.func,
  onChangeEndDate: PropTypes.func,
  canReset: PropTypes.bool,
  onReset: PropTypes.func,
  dateError: PropTypes.bool,
  onClickEvent: PropTypes.func,
};
